import { useMutation, useReactiveVar } from '@apollo/client'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { TextField, Typography } from '@material-ui/core'
import Paper from '@material-ui/core/Paper'
import { Theme, makeStyles } from '@material-ui/core/styles'
import DeleteOutlined from '@material-ui/icons/DeleteOutlined'
import { SetProjectContentItemElements } from 'api/graphQL/mutations/projectProductFamily.mutations'
import { userInfo } from 'api/graphQL/reactiveVars/user'
import {
    isContentTeam,
    isOmWorkspaceUser,
    workspaceContents,
} from 'api/graphQL/reactiveVars/workspaceContent'
import clsx from 'clsx'
import { ProductFamilyStudioManagement } from 'models/StudioManagement/studioManagement'
import { TextContentElementInput } from 'models/setProjectContentItemElementsInput'
import React from 'react'
import useGlobalSnackbar from 'shared/utils/useGlobalSnackbar'
import { AreaContext } from './Common/AreaContext'
import { TabsContext } from './Common/TabsContext'
import { getPropertyEditingDetails } from './Common/addUtils'
import { NormalizedDocumentTemplateViewItem } from './Common/model'
import {
    ProductTypeInFamilyViewContentType,
    WorkspaceTabTitles,
    isProductGroupIconShown,
    updatedNotesList,
} from './Common/utils'
interface StyleProps {
    isHidden: boolean
    calledFromImport?: boolean
    contentTeamFromStudioPage?: boolean
    isArticleLevelComment?: boolean
    parentProductTypeInFamilyViewContentType?: ProductTypeInFamilyViewContentType
}

const useShowHideBarStyles = makeStyles((theme: Theme) => ({
    paperNote: (props: StyleProps) => ({
        border: 'none',
        marginLeft: theme.spacing(3),
        marginTop: props.contentTeamFromStudioPage
            ? theme.spacing(3.125)
            : theme.spacing(1.5),
        marginBottom: props.contentTeamFromStudioPage
            ? theme.spacing(3.125)
            : 0,
        marginRight: theme.spacing(18),
        padding: props.contentTeamFromStudioPage
            ? theme.spacing(0.5, 0.5, 0, 1)
            : props.isArticleLevelComment
            ? 0
            : theme.spacing(2.25),
        background: isProductGroupIconShown(
            props.parentProductTypeInFamilyViewContentType
        )
            ? 'transparent'
            : theme.palette.common.white,
    }),
    noteTextArea: {
        fontSize: pxToRem(13),
        fontFamily: 'Roboto',
        fontWeight: 400,
        lineHeight: 1.585,
        '& .MuiInputBase-multiline': {
            padding: 0,
        },
    },
    note: (props: StyleProps) => ({
        background: props.isHidden ? 'transparent' : '#D9F3F9',
        color: props.isHidden ? '#04325B40' : '#04325B',
        fontSize: pxToRem(13),
        fontWeight: 400,
        width: '100%',
        border: 'none',
        outline: 'none',
        resize: 'none',
    }),
    actionIcon: (props: StyleProps) => ({
        color: props.isHidden ? '#04325B40' : '#04325B',
        position: 'absolute',
        padding: theme.spacing(0.5),
        right: 0,
        top: 0,
        zIndex: 2,
        cursor: 'pointer',
    }),
    notesContainer: (props: StyleProps) => ({
        padding: theme.spacing(2),
        marginTop: props.contentTeamFromStudioPage ? theme.spacing(1.5) : 0,
        marginBottom: !props.contentTeamFromStudioPage ? theme.spacing(1.5) : 0,
        borderRadius: 4,
        position: 'relative',
        background: props.isHidden ? '#d9f3f940' : '#D9F3F9',
    }),
}))

export interface SpecifierNoteProps {
    notes: string[]
    isHidden: boolean
    calledFromImport?: boolean
    isArticleLevelComment?: boolean
    getParentDetails?: () => {
        contentItemParentId: string
        parentKey: string
        parentType: string
    }
    editMode?: boolean
    template?: NormalizedDocumentTemplateViewItem
    selectedTopic?: Partial<ProductFamilyStudioManagement>
    contentTeamFromStudioPage?: boolean
    parentProductTypeInFamilyViewContentType?: ProductTypeInFamilyViewContentType
    isFocused?: boolean
    setCurrentSpecifierNoteId?: (templateId: string) => void
}

const SpecifierNote: React.FC<SpecifierNoteProps> = ({
    notes,
    isHidden,
    calledFromImport,
    isArticleLevelComment,
    getParentDetails,
    editMode,
    template,
    selectedTopic,
    contentTeamFromStudioPage,
    parentProductTypeInFamilyViewContentType,
    isFocused,
    setCurrentSpecifierNoteId,
}) => {
    const classes = useShowHideBarStyles({
        isHidden,
        calledFromImport,
        contentTeamFromStudioPage,
        isArticleLevelComment,
        parentProductTypeInFamilyViewContentType,
    })
    const [localNotes, setLocalNotes] = React.useState<string[]>([])
    const { handleUpdateTemplate } = React.useContext(TabsContext)
    const isContentTeamUser = useReactiveVar(isContentTeam)
    const isOMUser = useReactiveVar(isOmWorkspaceUser)

    const { isAdmin } = useReactiveVar(userInfo)
    const { selectedProjectNode, projectId } = React.useContext(AreaContext)
    const { showGlobalSnackbar } = useGlobalSnackbar()

    const [selectedNote, setSelectedNote] = React.useState<string>('')
    const [maxRow, setMaxRow] = React.useState<number>(4)
    const onNoteClick = React.useCallback(
        (note: string) => (event: React.MouseEvent<HTMLElement>) => {
            if (editMode) {
                setSelectedNote(note)

                const offsetHeight = event.currentTarget.offsetHeight
                const calculatedLines = Math.ceil(offsetHeight / 22) // 22 is a calculated number to find the line number

                const newMaxRow = calculatedLines > 4 ? calculatedLines : 4

                setMaxRow(newMaxRow)
            }
        },
        [editMode]
    )

    const [
        updateProjectContentItemElement,
        { loading: updateNotesLoading },
    ] = useMutation(SetProjectContentItemElements(6), {
        fetchPolicy: 'no-cache',
        onCompleted: (data) => {
            showGlobalSnackbar({
                variables: {
                    message: 'Changes Saved Successfully',
                    variant: 'success',
                    id: `ContentItemElementText-UpdateSpecifierSuccess`,
                },
            })
        },
        onError: (error) => {
            if (error.networkError) {
                showGlobalSnackbar({
                    variables: {
                        message:
                            'There was a problem saving your changes. Please copy your recent edits and reconnect to the network.',
                        variant: 'error',
                        id: 'WorkspacePage_SetProjectContentItemElementError',
                    },
                })
            } else if (error.message.includes('character')) {
                showGlobalSnackbar({
                    variables: {
                        message: `There was a problem saving your changes. Please remove "|" to save your changes.`,
                        variant: 'error',
                        id: 'WorkspacePage_SetProjectContentItemElementError',
                    },
                })
            } else {
                showGlobalSnackbar({
                    variables: {
                        message: `There was a problem saving your changes.`,
                        variant: 'error',
                        id: 'WorkspacePage_SetProjectContentItemElementError',
                    },
                })
            }
        },
    })
    const handleChangeNoteValue = (event: any, index: number) => {
        const updatedNotes = localNotes?.map((item, localIndex) => {
            if (localIndex === index) {
                return event.target.value
            } else return item
        })
        setLocalNotes(updatedNotes)

        setSelectedNote(event.target.value)
    }

    const onBlurNote = (
        updatedText: string,
        editingNote?: number,
        deletedNoteIndex?: number,
        addNote?: boolean
    ) => {
        if (updateNotesLoading) return
        // Return if there is no changes in note
        if (notes[editingNote!] === updatedText) {
            setSelectedNote('')
            setCurrentSpecifierNoteId?.('')
            return
        }

        const updatedNotesArray: string[] = updatedNotesList(
            updatedText,
            editingNote,
            deletedNoteIndex,
            addNote,
            localNotes
        )!
        setLocalNotes(updatedNotesArray)
        handleUpdateTemplate?.(
            {
                ...template!,
                contentItem: {
                    ...template?.contentItem,
                    [isArticleLevelComment ? 'parentComments' : 'comments']:
                        notes?.length !== 0 &&
                        updatedNotesArray?.length === 0 &&
                        deletedNoteIndex === undefined
                            ? ['']
                            : updatedNotesArray!,
                },
            },
            true
        )

        const editingInput = getPropertyEditingDetails({
            selectedTopic,
            property: template!,
            isForArticle: template?.contentItem?.textType === 'Article',
            ...(getParentDetails
                ? getParentDetails()
                : // get parent details and providing default values ('') if handler is not provided
                  {
                      contentItemParentId: '',
                      parentKey: '',
                      parentType: 'None',
                  }),
            nodeId: selectedTopic
                ? selectedTopic?.projectElementNodeId!
                : selectedProjectNode?.id ?? '',
        })

        updateProjectContentItemElement({
            variables: {
                input: {
                    ...editingInput,
                    isFromStudioManagementModule: isContentTeamUser || isOMUser,
                    elements: template?.contentItem?.elements?.map(
                        (elementIterator) => {
                            return {
                                id: elementIterator.id,
                                imperialUnits: elementIterator.imperialUnits,
                                metricUnits: elementIterator.metricUnits,
                                optionItemState:
                                    elementIterator.optionItemState,
                                text: elementIterator.text,
                                type: elementIterator.type,
                                placeholderText:
                                    elementIterator.placeholderText,
                                changeType: elementIterator.changeType,
                                authoredUserId: elementIterator.authoredUserId,
                                elements: elementIterator?.elements?.map(
                                    (element) => {
                                        return {
                                            id: element.id,

                                            imperialUnits:
                                                element.imperialUnits,
                                            metricUnits: element.metricUnits,
                                            optionItemState:
                                                element.optionItemState,
                                            text: element.text,
                                            type: element.type,
                                            placeholderText: null,
                                            changeType: null,
                                            authoredUserId:
                                                element.authoredUserId,
                                        }
                                    }
                                ),
                            }
                        }
                    ) as Partial<TextContentElementInput>[],
                    specifierNotes:
                        notes?.length !== 0 &&
                        updatedNotesArray?.length === 0 &&
                        deletedNoteIndex === undefined
                            ? ['']
                            : updatedNotesArray!,
                    updateType: isArticleLevelComment
                        ? 'ArticleSpecifierNote'
                        : 'SpecifierNote',
                },
                partType: selectedTopic
                    ? 'CONTENTGROUP'
                    : WorkspaceTabTitles[workspaceContents()?.tabSelected ?? 0],
                projectId,
                rootArticleContentItemIds: [template?.articleId ?? ''],
                productFamilyId: selectedTopic
                    ? selectedTopic?.ppfId
                    : template?.documentTemplateView?.productFamilyId!,
            },
        }).then(() => {
            setSelectedNote('')
            setCurrentSpecifierNoteId?.('')
        })
    }

    React.useEffect(() => {
        if (notes) {
            setLocalNotes(notes)
        }
    }, [notes])

    return !notes || notes?.length === 0 ? (
        <></>
    ) : (
        <Paper className={classes.paperNote} variant="outlined">
            <GridContainer
                direction={'column'}
                spacing={2}
                data-testid="specifierNote"
            >
                {localNotes?.map((note: string, index) => (
                    <div
                        className={classes.notesContainer}
                        style={{
                            border:
                                isFocused && index === 0
                                    ? '2px solid #04325B40'
                                    : 'none',
                        }}
                        key={`specifierNote-${index}`}
                    >
                        {contentTeamFromStudioPage &&
                            (isOMUser || !isAdmin) && (
                                <DeleteOutlined
                                    className={classes.actionIcon}
                                    onClick={() =>
                                        onBlurNote('', undefined, index)
                                    }
                                    data-testid="removeNoteIcon"
                                />
                            )}
                        {(editMode && selectedNote === note) ||
                        (isFocused && index === 0) ? (
                            <TextField
                                multiline
                                rowsMax={maxRow}
                                value={note}
                                placeholder="Insert note here..."
                                onChange={(e) =>
                                    handleChangeNoteValue(e, index)
                                }
                                onBlur={(e) =>
                                    onBlurNote(e.target.value, index)
                                }
                                className={clsx(
                                    classes.note,
                                    classes.noteTextArea
                                )}
                                autoFocus={isFocused && index === 0}
                                classes={{
                                    root: clsx(
                                        classes.note,
                                        classes.noteTextArea
                                    ),
                                }}
                                InputProps={{
                                    disableUnderline: true,
                                }}
                                inputProps={{
                                    'data-testid': 'noteEditorInput',
                                    className: clsx(
                                        classes.note,
                                        classes.noteTextArea
                                    ),
                                }}
                                data-testid="actualNoteEditor"
                            />
                        ) : (
                            <Typography
                                onClick={onNoteClick(note)}
                                component="span"
                                className={classes.note}
                                data-testid="actualNote"
                            >
                                {note}
                            </Typography>
                        )}
                    </div>
                ))}
            </GridContainer>
        </Paper>
    )
}

export default SpecifierNote
